import { useQuery } from '@tanstack/react-query';
import { Country } from 'App/i18n/enums';
import {
  bankingService,
  FeatureFlag,
  featureFlagService,
} from 'commons/services';
import useStoreInfo from './useStoreInfo';

interface UseProviderWithdrawalDisabledResult {
  disabled: boolean;
  loading: boolean;
}

/**
 * Per-provider withdrawal kill switch. A merchant withdraws either through
 * banking or through the payment provider; this resolves the
 * matching switch:
 *
 * - `fs-pagonube-banking-withdrawals-disabled`          — banking provider
 * - `fs-pagonube-payment-provider-withdrawals-disabled` — payment provider
 *
 * Only runs for countries where withdrawals split across providers (today: AR).
 * For all other countries returns { disabled: false, loading: false } with no
 * extra fetches.
 */
export function useProviderWithdrawalDisabled(): UseProviderWithdrawalDisabledResult {
  const { storeInfo } = useStoreInfo();

  // Per-provider shutdown only applies where banking eligibility is owned by a
  // payments service we can ask. Today that's AR (RPG); add a country here when
  // its withdrawals split across providers too.
  const COUNTRIES_WITH_PROVIDER_WITHDRAWAL_SHUTDOWN = [Country.ARGENTINA];

  const hasProviderShutdown =
    COUNTRIES_WITH_PROVIDER_WITHDRAWAL_SHUTDOWN.includes(
      storeInfo.country as Country,
    );

  const bankingEnabledQuery = useQuery({
    queryKey: ['bankingEnabledQuery', storeInfo.id],
    queryFn: () => bankingService.isBankingEnabled(storeInfo.id),
    enabled: hasProviderShutdown,
  });

  const isBanking = bankingEnabledQuery.isSuccess && !!bankingEnabledQuery.data;

  const bankingDisabledQuery = useQuery({
    queryKey: [
      `${FeatureFlag.FS_PAGONUBE_BANKING_WITHDRAWALS_DISABLED}EnabledQuery`,
    ],
    queryFn: () =>
      featureFlagService.isFeatureFlagEnabled(
        FeatureFlag.FS_PAGONUBE_BANKING_WITHDRAWALS_DISABLED,
        storeInfo.country,
      ),
    enabled: hasProviderShutdown && isBanking,
  });

  const paymentProviderDisabledQuery = useQuery({
    queryKey: [
      `${FeatureFlag.FS_PAGONUBE_PAYMENT_PROVIDER_WITHDRAWALS_DISABLED}EnabledQuery`,
    ],
    queryFn: () =>
      featureFlagService.isFeatureFlagEnabled(
        FeatureFlag.FS_PAGONUBE_PAYMENT_PROVIDER_WITHDRAWALS_DISABLED,
        storeInfo.country,
      ),
    enabled: hasProviderShutdown && bankingEnabledQuery.isSuccess && !isBanking,
  });

  if (!hasProviderShutdown) {
    return { disabled: false, loading: false };
  }

  const providerWithdrawalDisabled = isBanking
    ? bankingDisabledQuery.isSuccess && !!bankingDisabledQuery.data
    : paymentProviderDisabledQuery.isSuccess &&
      !!paymentProviderDisabledQuery.data;

  return {
    disabled: providerWithdrawalDisabled,
    loading:
      bankingEnabledQuery.isInitialLoading ||
      bankingDisabledQuery.isInitialLoading ||
      paymentProviderDisabledQuery.isInitialLoading,
  };
}
